/* Project Stats — reveal and count-up.

   One trigger drives both, so a block fades up at the same moment its numbers
   start counting rather than the two happening at different scroll positions.
   (The theme's own animations.js adds .fade-in as soon as a section's edge
   touches the viewport, which is far too early for a number to be read.)

   A "unit" is whatever should move as one piece: the headline, the lede, a
   whole ledger, or an individual stat in the other layouts. Each unit reveals
   and fires any counts inside it together.

   Count-up animates only the numeric run inside a value and leaves everything
   around it alone, so "150%", "100%+", "+20,000" and "+55% Net MER" all count
   correctly and land on their exact source text. */

(function () {
  'use strict';

  // Grouped ("20,000", "1,299.99") first, then plain ("150", "1.5").
  var NUMBER = /-?\d{1,3}(?:,\d{3})+(?:\.\d+)?|-?\d+(?:\.\d+)?/;
  var DEFAULT_DURATION = 1500;
  var START_DELAY = 90; // the digits move as the number settles, not after it

  function prefersReducedMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function parse(text) {
    var match = text.match(NUMBER);
    if (!match) return null;

    var raw = match[0];
    var value = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(value) || !isFinite(value)) return null;

    var dot = raw.indexOf('.');

    return {
      prefix: text.slice(0, match.index),
      suffix: text.slice(match.index + raw.length),
      value: value,
      decimals: dot === -1 ? 0 : raw.length - dot - 1,
      grouped: raw.indexOf(',') !== -1
    };
  }

  function format(n, decimals, grouped) {
    var out = n.toFixed(decimals);
    if (!grouped) return out;

    var parts = out.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  function count(el) {
    if (el.getAttribute('data-stat-counted') === 'true') return;
    el.setAttribute('data-stat-counted', 'true');

    var original = el.textContent;
    var stat = parse(original);
    if (!stat) return; // no number in there — leave the text as authored

    var duration = parseInt(el.getAttribute('data-stat-duration'), 10);
    if (isNaN(duration) || duration <= 0) duration = DEFAULT_DURATION;

    el.classList.add('is-counting');
    el.textContent = stat.prefix + format(0, stat.decimals, stat.grouped) + stat.suffix;

    var start = null;

    function step(now) {
      if (start === null) start = now;

      var t = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - t, 4);

      if (t < 1) {
        el.textContent =
          stat.prefix + format(stat.value * eased, stat.decimals, stat.grouped) + stat.suffix;
        window.requestAnimationFrame(step);
      } else {
        el.textContent = original; // land on the exact authored string
        el.classList.remove('is-counting');
      }
    }

    window.setTimeout(function () {
      window.requestAnimationFrame(step);
    }, START_DELAY);
  }

  function reveal(unit) {
    if (unit.getAttribute('data-stats-revealed') === 'true') return;
    unit.setAttribute('data-stats-revealed', 'true');

    unit.classList.add('is-revealed');

    if (prefersReducedMotion()) return;

    // The unit may be the value itself (classic layout), not just contain one.
    if (unit.hasAttribute('data-stat-value')) count(unit);

    var vals = unit.querySelectorAll('[data-stat-value]');
    for (var i = 0; i < vals.length; i++) count(vals[i]);
  }

  // What moves as one piece. A ledger reveals whole; elsewhere each stat does.
  function units(scope) {
    var list = [].slice.call(
      scope.querySelectorAll(
        '.stats-layout__headline, .stats-layout__lede, .stats-layout__ledger'
      )
    );

    var stats = scope.querySelectorAll('.stats-layout__stat');
    for (var i = 0; i < stats.length; i++) {
      if (!stats[i].closest('.stats-layout__ledger')) list.push(stats[i]);
    }

    // The classic layout has none of those wrappers, so any value not already
    // covered stands as its own unit. That keeps count-up working there too.
    var vals = scope.querySelectorAll('[data-stat-value]');
    for (var j = 0; j < vals.length; j++) {
      var covered = false;
      for (var k = 0; k < list.length; k++) {
        if (list[k].contains(vals[j])) {
          covered = true;
          break;
        }
      }
      if (!covered) list.push(vals[j]);
    }

    return list;
  }

  var observer = null;

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          reveal(entry.target.statsUnit || entry.target);
        });
      },
      // Shrink the viewport's bottom edge by a quarter so a unit has to rise
      // properly onto the screen first. A percentage threshold would instead
      // fire the moment a fraction of it clipped the bottom edge — for a big
      // number that is a few pixels, and the count would be over before it was
      // readable. threshold 0 keeps this correct for any height, including a
      // unit taller than the viewport.
      { threshold: 0, rootMargin: '0px 0px -25% 0px' }
    );

    return observer;
  }

  function init(root) {
    var list = units(root || document);
    if (!list.length) return;

    var obs = getObserver();

    for (var i = 0; i < list.length; i++) {
      var unit = list[i];
      if (unit.getAttribute('data-stats-bound') === 'true') continue;
      unit.setAttribute('data-stats-bound', 'true');

      // No observer support, or motion turned down: show it straight away.
      if (!obs || prefersReducedMotion()) {
        reveal(unit);
        continue;
      }

      // Watch the first number rather than the unit's top edge: a tall unit
      // like a ledger starts intersecting long before its numbers are on
      // screen. The whole unit still reveals as one piece.
      var trigger = unit.querySelector('[data-stat-value]') || unit;
      trigger.statsUnit = unit;
      obs.observe(trigger);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
